import React, { useEffect, useMemo, useState } from "react";
import { LOS_API_BASE_URL } from "@/config";
import { productsApi } from "@/api/products.api";
import { agentApi } from "@/api";
import type { Product } from "@/types/products.types";

interface Props {
  onClose: () => void;
  // Card mode: product + affiliateCode supplied → no API calls.
  affiliateCode?: string;
  product?: { id: string; name: string; category: string };
  // Standalone mode: pass agentId; modal fetches products + the agent's code.
  agentId?: string;
}

/**
 * Create UTM Link modal.
 *
 * Link contract (B2 / C7):
 *   Always: ac, product, src=dsa (campaign defaults to "direct" server-side)
 *   Optional: sub, utm_source, utm_term, utm_content
 *   (utm_medium left out of UI — YAGNI; can be added the same way later)
 *
 * Optional fields use progressive disclosure: chip → input, not empty fields.
 */
export const CreateUtmLinkModal: React.FC<Props> = ({
  onClose,
  affiliateCode: affiliateCodeProp,
  product,
  agentId,
}) => {
  const standalone = !product;

  const [products, setProducts] = useState<Product[]>([]);
  const [affiliateCode, setAffiliateCode] = useState(affiliateCodeProp || "");
  const [category, setCategory] = useState(product?.category || "");
  const [productId, setProductId] = useState(product?.id || "");
  const [sub, setSub] = useState("");
  const [copied, setCopied] = useState(false);

  // Optional params — only appear when the user opens them.
  const [showSource, setShowSource] = useState(false);
  const [showTerm, setShowTerm] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [utmSource, setUtmSource] = useState("");
  const [utmTerm, setUtmTerm] = useState("");
  const [utmContent, setUtmContent] = useState("");

  useEffect(() => {
    if (!standalone) return;
    productsApi.getProducts({ channel: "AGENT" }).then((r) => {
      if (r.status === "Success" && Array.isArray(r.response)) setProducts(r.response);
    });
    if (!affiliateCodeProp && agentId) {
      agentApi
        .getAgentDetails({ query_type: "get_agent_details", agent_id: agentId })
        .then((r) => {
          const d = r.response?.[0];
          if (d?.affiliate_code) setAffiliateCode(String(d.affiliate_code));
        });
    }
  }, [standalone, affiliateCodeProp, agentId]);

  const categories = useMemo(
    () => Array.from(new Set(products.map((p) => p.category).filter(Boolean))),
    [products]
  );
  const productsInCategory = useMemo(
    () => products.filter((p) => p.category === category),
    [products, category]
  );

  const cleanSub = sub.toUpperCase().replace(/[^A-Z0-9]/g, "");

  const link = useMemo(() => {
    if (!affiliateCode || !productId) return "";
    const params = new URLSearchParams({
      ac: affiliateCode,
      product: productId,
      src: "dsa",
    });
    if (cleanSub) params.set("sub", cleanSub);
    if (showSource && utmSource.trim()) params.set("utm_source", utmSource.trim());
    if (showTerm && utmTerm.trim()) params.set("utm_term", utmTerm.trim());
    if (showContent && utmContent.trim()) params.set("utm_content", utmContent.trim());
    return `${LOS_API_BASE_URL}/utm/link?${params.toString()}`;
  }, [
    affiliateCode,
    productId,
    cleanSub,
    showSource,
    showTerm,
    showContent,
    utmSource,
    utmTerm,
    utmContent,
  ]);

  const copy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const removeOptional = (which: "source" | "term" | "content") => {
    if (which === "source") {
      setShowSource(false);
      setUtmSource("");
    } else if (which === "term") {
      setShowTerm(false);
      setUtmTerm("");
    } else {
      setShowContent(false);
      setUtmContent("");
    }
  };

  const optionalChips: Array<{
    key: "source" | "term" | "content";
    label: string;
    visible: boolean;
    open: () => void;
  }> = [
    {
      key: "source",
      label: "+ Add Source",
      visible: !showSource,
      open: () => setShowSource(true),
    },
    {
      key: "term",
      label: "+ Add Term",
      visible: !showTerm,
      open: () => setShowTerm(true),
    },
    {
      key: "content",
      label: "+ Add Content",
      visible: !showContent,
      open: () => setShowContent(true),
    },
  ];
  const anyChip = optionalChips.some((c) => c.visible);

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18 }}>Create UTM Link</h2>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "none",
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        {standalone ? (
          <>
            <label style={labelStyle}>Select Category</label>
            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setProductId("");
              }}
              style={inputStyle}
            >
              <option value=""> Select Category </option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <label style={labelStyle}>Select Product</label>
            <select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              disabled={!category}
              style={inputStyle}
            >
              <option value=""> Select Product </option>
              {productsInCategory.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </>
        ) : (
          <div
            style={{
              marginBottom: 16,
              padding: "10px 12px",
              background: "#f3f4f6",
              borderRadius: 8,
            }}
          >
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              {product!.category}
            </div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{product!.name}</div>
          </div>
        )}

        <label style={labelStyle}>
          Sub Code (your code {affiliateCode || "..."} + team chars)
        </label>
        <input
          value={cleanSub}
          onChange={(e) => setSub(e.target.value)}
          placeholder="e.g. 001"
          maxLength={6}
          style={inputStyle}
        />

        {/* Progressive disclosure: optional marketing UTM labels */}
        {showSource && (
          <OptionalField
            label="UTM Source"
            value={utmSource}
            onChange={setUtmSource}
            placeholder="e.g. youtube"
            onRemove={() => removeOptional("source")}
          />
        )}
        {showTerm && (
          <OptionalField
            label="UTM Term"
            value={utmTerm}
            onChange={setUtmTerm}
            placeholder="e.g. diwali"
            onRemove={() => removeOptional("term")}
          />
        )}
        {showContent && (
          <OptionalField
            label="UTM Content"
            value={utmContent}
            onChange={setUtmContent}
            placeholder="e.g. video1"
            onRemove={() => removeOptional("content")}
          />
        )}

        {anyChip && (
          <div style={chipRow}>
            {optionalChips
              .filter((c) => c.visible)
              .map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={c.open}
                  style={chipStyle}
                >
                  {c.label}
                </button>
              ))}
          </div>
        )}

        {link && (
          <div
            style={{
              marginTop: 12,
              padding: 10,
              background: "#f3f4f6",
              borderRadius: 8,
              fontSize: 12,
              wordBreak: "break-all",
            }}
          >
            {link}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 16,
          }}
        >
          <button
            onClick={copy}
            disabled={!link}
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              border: "none",
              background: link ? "#1f2d3d" : "#9ca3af",
              color: "#fff",
              cursor: link ? "pointer" : "not-allowed",
            }}
          >
            {copied ? "Copied!" : "Copy Link"}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Expanded optional field with a clear/remove control. */
const OptionalField: React.FC<{
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  onRemove: () => void;
}> = ({ label, hint, value, onChange, placeholder, onRemove }) => (
  <div style={{ marginBottom: 12 }}>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
      }}
    >
      <label style={labelStyle}>
        {label}
        {hint ? (
          <span style={{ fontWeight: 400, color: "#9ca3af" }}> — {hint}</span>
        ) : null}
      </label>
      <button
        type="button"
        onClick={onRemove}
        title="Remove"
        style={removeBtn}
      >
        ×
      </button>
    </div>
    <input
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...inputStyle, marginBottom: 0 }}
    />
  </div>
);

const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};
const card: React.CSSProperties = {
  background: "#fff",
  borderRadius: 12,
  padding: 24,
  width: 460,
  maxWidth: "90vw",
  color: "#111",
  maxHeight: "90vh",
  overflowY: "auto",
};
const labelStyle: React.CSSProperties = { fontSize: 12, color: "#555" };
const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  marginTop: 4,
  marginBottom: 12,
  border: "1px solid #d1d5db",
  borderRadius: 8,
  fontSize: 14,
  boxSizing: "border-box",
};
const chipRow: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  marginTop: 4,
  marginBottom: 4,
};
const chipStyle: React.CSSProperties = {
  padding: "6px 12px",
  borderRadius: 999,
  border: "1px dashed #9ca3af",
  background: "#f9fafb",
  color: "#374151",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
};
const removeBtn: React.CSSProperties = {
  border: "none",
  background: "none",
  color: "#9ca3af",
  fontSize: 16,
  lineHeight: 1,
  cursor: "pointer",
  padding: "0 4px",
};
